import { useState, useEffect } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function calcEnd(start, months) {
  if (!start) return "";
  const [y, m, d] = start.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1 + Number(months), d));
  if (t.getUTCDate() !== d) t.setUTCDate(0);
  t.setUTCDate(t.getUTCDate() - 1);
  return t.toISOString().slice(0, 10);
}

export default function TermDialog({ placement, renewal, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({});
  const [overrideEnd, setOverrideEnd] = useState(false);
  useEffect(() => {
    if (!open || !placement) return;
    const prevEnd = placement.contract?.end_date;
    let start = new Date().toISOString().slice(0, 10);
    if (renewal && prevEnd) { const d = new Date(prevEnd + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + 1); start = d.toISOString().slice(0, 10); }
    setF({ term_months: "12", start_date: start, monthly_price: placement.monthly_price ?? "", notes: "" });
    setOverrideEnd(false);
  }, [open, placement, renewal]);
  const end = overrideEnd ? f.end_date : calcEnd(f.start_date, f.term_months);

  const save = async () => {
    try {
      await api.post(`/placements/${placement.id}/terms`, {
        term_months: Number(f.term_months), start_date: f.start_date, end_date: end,
        monthly_price: f.monthly_price === "" ? null : Number(f.monthly_price), is_renewal: !!renewal, notes: f.notes,
      });
      toast.success(renewal ? "Renewal term added — previous contract kept in history" : "Contract term added");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="term-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{renewal ? "Renew" : "Add Contract Term"} · {placement?.zone}</DialogTitle>
          <DialogDescription>Creates a new contract through the normal contract flow. Earlier terms are never overwritten.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-1">
          <div><Label className="text-xs">Contract length</Label>
            <Select value={f.term_months} onValueChange={(v) => setF({ ...f, term_months: v })}>
              <SelectTrigger className="mt-1" data-testid="term-length"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="6">6 months</SelectItem><SelectItem value="12">12 months</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-xs">Monthly price</Label><Input type="number" className="mt-1" value={f.monthly_price ?? ""} onChange={(e) => setF({ ...f, monthly_price: e.target.value })} data-testid="term-monthly" /></div>
          <div><Label className="text-xs">Start date</Label><Input type="date" className="mt-1" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="term-start" /></div>
          <div><Label className="text-xs">End date {overrideEnd ? "(override)" : "(auto)"}</Label>
            <Input type="date" className="mt-1" value={end || ""} disabled={!overrideEnd} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="term-end" />
            <button type="button" className="mt-1 text-[11px] text-[#0094A8] hover:underline" onClick={() => { setOverrideEnd(!overrideEnd); setF({ ...f, end_date: end }); }} data-testid="term-end-override">{overrideEnd ? "Use automatic end date" : "Override end date"}</button></div>
          <div className="col-span-2"><Label className="text-xs">{renewal ? "Renewal notes" : "Notes"}</Label><Textarea rows={2} className="mt-1" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="term-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} data-testid="term-save">{renewal ? "Save Renewal" : "Save Term"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
